import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Grid } from "@mui/material";
import SimpleTextField from "@/Layouts/components/InputComponents/SimpleTextField";
import { ICITMaxGraphValues, ISystemSettings } from "@/Settings/types/Common/model";
import { errorLogger } from "@/Common/helpers/logger";
import InfoSubTitle from "../../../common/InfoSubTitle";
import Title from "../../../common/Title";
import { updateSystemSettings } from "@/Settings/api/SystemSettings/mutations";

type TMaxUnfundedProps = {
	disabled: boolean;
	locationId: string;
	citMaxGraphValues?: ICITMaxGraphValues;
};

const MaxUnfundedDollars = (props: TMaxUnfundedProps) => {
	const { disabled, locationId, citMaxGraphValues } = props;
	const queryClient = useQueryClient();
	const { mutate, isPending, isError } = useMutation({
		mutationKey: ["systemSettings", locationId],
		mutationFn: (input: { citMaxGraphValues: Partial<ICITMaxGraphValues> }) =>
			updateSystemSettings(locationId, input, "CIT_MAX_UNFUNDED_DOLLARS"),
		onError: (error) => errorLogger(error),
		onSuccess: (data: ISystemSettings) =>
			queryClient.setQueryData(["systemSettings", locationId], (oldData: ISystemSettings) => ({
				...oldData,
				citMaxGraphValues: { ...oldData?.citMaxGraphValues, maxUnfundedDollars: data.citMaxGraphValues.maxUnfundedDollars },
			})),
	});

	const handleMutation = (value: string) => {
		const inputValue = Math.floor(+value || 0);
		const update = {
			citMaxGraphValues: {
				...citMaxGraphValues,
				maxUnfundedDollars: inputValue,
			},
		};
		mutate(update);
	};

	return (
		<>
			<InfoSubTitle
				title="Max Unfunded Amount ($)"
				info="Define the maximum amount of unfunded dollars (in Millions)"
				showInfo
				className="!mb-[12px]"
			/>
			<SimpleTextField
				sx={{ width: "150px" }}
				editable
				outlined
				text={String(citMaxGraphValues?.maxUnfundedDollars) || "0"}
				placeholder="Amount"
				error={isError}
				isLoading={isPending}
				onActionUpdate={handleMutation}
				onlyNumbers
				disabled={disabled}
				startAdornment="$"
				endAdornment="Million"
				inputProps={{ maxLength: 2 }}
			/>
		</>
	);
};

const MaxUnfundedContracts = (props: TMaxUnfundedProps) => {
	const { disabled, locationId, citMaxGraphValues } = props;
	const queryClient = useQueryClient();

	const { mutate, isPending, isError } = useMutation({
		mutationKey: ["systemSettings", locationId],
		mutationFn: (input: { citMaxGraphValues: Partial<ICITMaxGraphValues> }) =>
			updateSystemSettings(locationId, input, "CIT_MAX_UNFUNDED_CONTRACTS"),
		onError: (error) => errorLogger(error),
		onSuccess: (data: ISystemSettings) =>
			queryClient.setQueryData(["systemSettings", locationId], (oldData: ISystemSettings) => ({
				...oldData,
				citMaxGraphValues: { ...oldData.citMaxGraphValues, maxUnfundedContracts: data.citMaxGraphValues.maxUnfundedContracts },
			})),
	});

	const handleMutation = (value: string) => {
		const inputValue = Math.floor(+value || 0);
		const update = {
			citMaxGraphValues: {
				...citMaxGraphValues,
				maxUnfundedContracts: inputValue,
			},
		};
		mutate(update);
	};

	return (
		<>
			<InfoSubTitle
				title="Max Unfunded Contracts"
				info="Define the maximum amount of unfunded contracts in transit"
				showInfo
				className="!mb-[12px]"
			/>
			<SimpleTextField
				sx={{ width: "150px" }}
				editable
				outlined
				text={String(citMaxGraphValues?.maxUnfundedContracts) || "0"}
				placeholder={"Enter Number Of Contracts Allowed"}
				error={isError}
				isLoading={isPending}
				onActionUpdate={handleMutation}
				onlyNumbers
				disabled={disabled}
				inputProps={{ maxLength: 3 }}
			/>
		</>
	);
};

type TCitMaxGraphValuesProps = {
	action: boolean;
	locationId: string;
	citMaxGraphValues?: ICITMaxGraphValues;
};

const CitMaxGraphValues = (props: TCitMaxGraphValuesProps) => {
	const { action, locationId, citMaxGraphValues } = props;
	return (
		<>
			<Title title="CIT Max Graph Values" className="!mb-[24px]" />
			<Grid container className="border-none" columnSpacing={2}>
				<Grid item xs={4}>
					<MaxUnfundedDollars disabled={!action} locationId={locationId} citMaxGraphValues={citMaxGraphValues} />
				</Grid>
				<Grid item xs={4}>
					<MaxUnfundedContracts disabled={!action} locationId={locationId} citMaxGraphValues={citMaxGraphValues} />
				</Grid>
			</Grid>
		</>
	);
};

export default CitMaxGraphValues;
